import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of, switchMap, takeWhile } from 'rxjs';

import { IPanel, PanelsService } from '@app/core/panels';
import { IReport, ReportsService } from '@app/core/reports';
import { DialogService } from 'primeng/dynamicdialog';
import { PanelDialogComponent } from '../panel-dialog/panel-dialog.component';

@Component({
  selector: 'app-panel-manager',
  templateUrl: './panel-manager.component.html',
  styleUrls: ['./panel-manager.component.css']
})
export class PanelManagerComponent implements OnInit, OnDestroy {

  public selectedReports: IReport[];
  public availableReports: IReport[];
  public form: FormGroup;

  private allReports: IReport[];
  private destroyed: boolean;
  private panel: IPanel;
  private draggedReport: IReport;

  constructor(
    private panelService: PanelsService,
    private reportsService: ReportsService,
    private dialogService: DialogService,
    private route: ActivatedRoute
  ) {
    this.selectedReports = [];
    this.availableReports = [];

    this.form = new FormGroup({
      title: new FormControl({ value: '', disabled: true}, [Validators.required]),
      allowedProfiles: new FormControl({ value: '', disabled: true}, [Validators.required]),
    });

  }

  public ngOnInit(): void {

    this.route.data.pipe(
      switchMap(data => of(data['panel'] as IPanel)),
      switchMap(panel => {

        this.panel = panel;

        return forkJoin([
          this.reportsService.getReports(),
          this.panelService.getReportsByPanelId(panel.id)
        ]);

      }),
      takeWhile(() => !this.destroyed)
    )
    .subscribe(values => {

      const [allReports, panelReports] = values;
      this.form.patchValue(this.panel);

      this.allReports = allReports;

      this.selectedReports = this.allReports.filter(r => panelReports.some(pr => pr.reportId === r.id));
      this.availableReports = this.allReports.filter(r => !panelReports.some(pr => pr.reportId === r.id));

    });

  }

  public dragStart(report: IReport) {
    this.draggedReport = report;
  }

  public dragEnd() {
    this.draggedReport = null;
  }

  public drop() {

    const draggedReport = this.draggedReport;
    if (draggedReport) {

      this.panelService.addReport({
        panelId: this.panel.id,
        reportId: draggedReport.id,
      })
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe(() => {
        const draggedReportIndex = this.availableReports.findIndex(r => r.id === draggedReport.id);
        this.selectedReports = [...this.selectedReports, draggedReport];
        this.availableReports = this.availableReports.filter((val, i) => i != draggedReportIndex);
        this.draggedReport = null;
      });
    }
  }

  public deleteReport(report: IReport): void {

    this.panelService.deleteReport({
      panelId: this.panel.id,
      reportId: report.id
    })
    .pipe(takeWhile(() => !this.destroyed))
    .subscribe(() => {
      this.selectedReports.splice(this.selectedReports.findIndex(r => r.id === report.id), 1);
      this.selectedReports = [...this.selectedReports];
      this.availableReports = [...this.availableReports, report];
    });

  }

  public editProperties(): void {

    this.dialogService.open(PanelDialogComponent, {
      header: 'Propiedades del panel',
      height: '600px',
      width: '800px',
      data: {
        panel: this.panel
      }
    })
    .onClose
    .pipe(takeWhile(() => !this.destroyed))
    .subscribe(panel => {

      if (panel) {
        this.panel = panel;
        this.form.patchValue(panel);
      }

    })

  }

  public ngOnDestroy() {
    this.destroyed = true;
  }

}
